// CalendarHeader.js

import React from 'react';
import dayjs from 'dayjs';
import { LuRefreshCcw } from 'react-icons/lu';
import RadioButtons from '../Tools/Radio/RadioButtons';
import { formatCustomDate } from '../../utils/date';
import { IoSettings } from 'react-icons/io5';
import Toggle from '../../ui/toggle';
import { GrFormPrevious, GrFormNext } from 'react-icons/gr';
import { Button } from '../../ui/button';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

const CalendarHeader = ({
    viewMode,
    setViewMode,
    monthIndex,
    handlePrevMonth,
    handleNextMonth,
    handleReset,
    weekStartDate,
    handlePrevWeek,
    handleNextWeek,
    showHistory,
    showScheduled,
    setShowHistory,
    setShowScheduled,
    showTodo,
    setShowTodo,
}) => {
    const currentMonthIndex = dayjs().month();
    const isCurrentWeek = weekStartDate.isSame(dayjs().startOf('week'), 'day');
    const navigate = useNavigate();

    // Helper to determine if reset button should be shown/active
    const showReset = (viewMode === 'month' && monthIndex !== currentMonthIndex) ||
        (viewMode === 'week' && !isCurrentWeek);

    // Helper for date display
    const getDateDisplay = () => {
        if (viewMode === 'month') {
            return dayjs(new Date(dayjs().year(), monthIndex)).format('MMMM YYYY');
        }
        if (viewMode === 'week') {
            return `${formatCustomDate(weekStartDate)} - ${weekStartDate.endOf('week').format('DD / MM')}`;
        }
        if (viewMode === 'list') {
            return 'All uploads';
        }
        return '';
    };

    const onPrev = viewMode === 'month' ? handlePrevMonth : handlePrevWeek;
    const onNext = viewMode === 'month' ? handleNextMonth : handleNextWeek;

    return (
        <header className="flex flex-col lg:flex-row items-center justify-between gap-2 p-4 bg-white border-b border-gray-100 shadow-sm dashboard-section">
            {/* Left Section: View Switcher */}
            <div className="flex-shrink-0">
                <RadioButtons viewMode={viewMode} setViewMode={setViewMode} />
            </div>

            {/* Center Section: Navigation & Date */}
            <div className="flex items-center gap-4 flex-1 justify-center min-w-0">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onPrev}
                        className="w-10 h-10 rounded-full border-gray-200 text-gray-600 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                    >
                        <GrFormPrevious size={22} />
                    </Button>

                    <p className="text-xl sm:text-3xl font-bold text-gray-800 whitespace-nowrap min-w-[180px] text-center select-none">
                        {getDateDisplay()}
                    </p>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onNext}
                        className="w-10 h-10 rounded-full border-gray-200 text-gray-600 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                    >
                        <GrFormNext size={22} />
                    </Button>
                </div>

                <div className={`transition-opacity duration-200 ${showReset ? 'opacity-100' : 'hidden'}`}>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReset}
                        className="text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 gap-1.5"
                        disabled={!showReset}
                    >
                        <LuRefreshCcw size={14} />
                        <span>Today</span>
                    </Button>
                </div>
            </div>

            {/* Right Section: Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
                <ShowHideDropdown
                    showHistory={showHistory}
                    showScheduled={showScheduled}
                    setShowHistory={setShowHistory}
                    setShowScheduled={setShowScheduled}
                    showTodo={showTodo}
                    setShowTodo={setShowTodo}
                    
                />
                
                <Button 
                    variant='default' 
                    size='default' 
                    className='flex items-center gap-2 shadow-md shadow-blue-200' 
                    onClick={() => navigate('/dashboard/upload')}
                >
                    <IoIosAddCircleOutline size={20} />
                    <span className="hidden sm:inline">New post</span>
                    <span className="sm:hidden">New</span>
                </Button>
            </div>
        </header>
    );
};

export default CalendarHeader;

const ShowHideDropdown = ({
    showHistory,
    showScheduled,
    setShowHistory,
    setShowScheduled,
    showTodo,
    setShowTodo,
}) => {
    return (
        <div className="relative z-20">
            <details className="group relative [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none ring-1 ring-gray-200 shadow-sm transition-all">
                    <IoSettings size={20} />
                </summary>

                <div className="absolute right-0 top-full mt-2 w-64 origin-top-right rounded-lg border border-gray-100 bg-white shadow-lg transition-all opacity-0 invisible group-open:visible group-open:opacity-100 group-open:translate-y-0 translate-y-2 duration-200">
                    <div className="p-4 space-y-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">View Options</h3>
                        
                        <FilterOption 
                            label="History" 
                            active={showHistory} 
                            onToggle={() => setShowHistory(prev => !prev)}
                            colorClass="bg-green-100 text-green-800"
                        />
                        
                        <FilterOption 
                            label="Scheduled" 
                            active={showScheduled} 
                            onToggle={() => setShowScheduled(prev => !prev)}
                            colorClass="bg-yellow-100 text-yellow-800"
                        />
                        
                        <FilterOption 
                            label="To-Do" 
                            active={showTodo} 
                            onToggle={() => setShowTodo(prev => !prev)}
                            colorClass="bg-blue-100 text-blue-800"
                        />
                    </div>
                </div>
            </details>
            
            {/* Backdrop to close on click outside - optional enhancement, 
                native details element doesn't support click-outside closing easily without JS 
                so we rely on user clicking the summary again or clicking another details.
            */}
        </div>
    );
};

const FilterOption = ({ label, active, onToggle, colorClass }) => (
    <div className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 transition-colors">
        <span className={`text-sm font-medium px-2 py-0.5 rounded ${colorClass}`}>
            {label}
        </span>
        <Toggle initial={active} onToggle={onToggle} />
    </div>
);
